import { getThemeManager } from "@omegagrid/core";
import type { Style, TableTheme } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import { TableThemeRef } from "@reporting/rp-types/analytics";
import rp from "../global";

/**
 * Table theme (og-grid TableTheme) utilities shared by the modules that store,
 * edit or render the table styles (P_STYLE type T): the stored JSON text, the
 * reference-vs-definition distinction of the view options and the copy of
 * a theme painted on a grid under the active application theme.
 */

/** a stored style reference ({id_style}) as opposed to an inline theme definition */
export function isTableThemeRef(theme: unknown): theme is TableThemeRef {
	return !!theme && typeof theme === 'object' && 'id_style' in theme;
}

/**
 * Parsed table theme of a style text. Throws on invalid JSON, returns null
 * when the JSON is not a theme definition (an array, a scalar, a reference).
 */
export function parseTableTheme(content: string): TableTheme|null {
	const parsed = JSON.parse(content);
	const isObject = !!parsed && typeof parsed === 'object' && !Array.isArray(parsed);
	return isObject && !isTableThemeRef(parsed) ? parsed as TableTheme : null;
}

/**
 * Parsed theme of a text typed into a raw JSON editor: the problem is
 * reported through the host alerts and null is returned when the text is
 * not a theme definition.
 */
export function parseTableThemeInput(content: string): TableTheme|null {
	let theme: TableTheme|null;
	try {
		theme = parseTableTheme(content);
	} catch (e) {
		rp.host.showAlert({type: 'danger', content: `${msg('Invalid JSON')}: ${(e as Error).message}`});
		return null;
	}

	if (!theme) rp.host.showAlert({type: 'danger', content: msg('Invalid theme')});
	return theme;
}

/** deep copy: the grid model keeps references to the items it applies, the source object stays serializable */
export function cloneTableTheme(theme: TableTheme): TableTheme {
	return JSON.parse(JSON.stringify(theme));
}

/**
 * Dark-mode contrast fixup of one style, in place: themes and the legacy cell
 * styles are authored for a light background, so a fill without a text color
 * gets black text and black text without a fill becomes white.
 */
export function fixStyleForDarkMode(style: Style): Style {
	if (style.fgColor && !style.color) style.color = '000000';
	if (!style.fgColor && style.color == '000000') style.color = 'FFFFFF';
	return style;
}

/** dark-mode copy of a theme with every item style fixed up; the original stays untouched */
export function normalizeThemeForDarkMode(theme: TableTheme): TableTheme {
	const clone = cloneTableTheme(theme);
	[...(clone.cells ?? []), ...(clone.columns ?? []), ...(clone.rows ?? [])].forEach(item => {
		if (item.style) fixStyleForDarkMode(item.style as Style);
	});
	return clone;
}

/** the copy of a theme to paint on a grid under the active application theme */
export function themeForActiveColorScheme(theme: TableTheme): TableTheme {
	return getThemeManager().activeTheme.type == 'dark'
		? normalizeThemeForDarkMode(theme)
		: cloneTableTheme(theme);
}
